import React, { useContext, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import SellOutlinedIcon from '@mui/icons-material/SellOutlined';
import StarBorderRoundedIcon from '@mui/icons-material/StarBorderRounded';
import StarRoundedIcon from '@mui/icons-material/StarRounded';
import {
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Chip,
  Divider,
  Grid,
  Typography
} from '@mui/material';
import { CategoryScale } from 'chart.js';
import Chart from 'chart.js/auto';
import {
  doc,
  getDoc,
  getDocs,
  onSnapshot,
  query,
  where
} from 'firebase/firestore';
import { Bar } from 'react-chartjs-2';
import { useTranslation } from 'react-i18next';

import { getMediaImage } from '../../api/aws/s3';
import { companiesRef } from '../../api/firebase/firestore/company';
import {
  addFavoriteMedia,
  deleteFavorite,
  favoriteMediasRef,
  mediasRef
} from '../../api/firebase/firestore/media';
import { usersRef } from '../../api/firebase/firestore/user';
import { getPost } from '../../api/microcms/article';
import noImage from '../../components/assets/noImage.svg';
import {
  black,
  blackText,
  blueGrayText,
  primaryButtonBackground,
  primarycolor
} from '../../constants/Colors';
import {
  categoryListForReference,
  funnelListForReference,
  mediaIndustriesList,
  mediaTypeList,
  targetList
} from '../../constants/Select';
import { screenType, select, status } from '../../constants/enum';
import { AuthContext } from '../../contexts/auth';
import { throwError } from '../../modules/error';

import ContactDrawer from './ContactDrawer';
import CustomSnackbar from './CustomSnackbar';

import type { SelectOption } from '../../constants/Select';

Chart.register(CategoryScale);

interface Props {
  title?: string;
  summaryText?: string;
  funnel?: number[];
  mediaType: number;
  target: number[];
  seasonalMenuMaterialText: string;
  industries: Array<{
    industriesNum: number;
    industriesOther: string;
    percentage: number;
  }>;
  image?: string;
  category?: number[] | number;
  demographicsGeneration?:
    | {
        f10: number;
        f20: number;
        f30: number;
        f40: number;
        f50: number;
        f60: number;
        f70: number;
        m10: number;
        m20: number;
        m30: number;
        m40: number;
        m50: number;
        m60: number;
        m70: number;
      }
    | undefined;
  demographicsGender:
    | {
        male: number;
        female: number;
      }
    | undefined;
  hasCheck?: boolean;
  mediaId: string;
  mediaName?: string;
  screen?: number;
  checked?: boolean;
  onCheckboxChange?: (id: string) => void;
  cantInquiry?: string;
  mediaStatus?: number;
}

const styles = {
  articleBox: {
    ':hover': {
      cursor: 'pointer',
      opacity: 0.7
    },
    '@media screen and (max-width:480px)': {
      padding: '8px'
    },
    bgcolor: '#F5F5F5',
    columnGap: '16px',
    display: 'flex',
    flexDirection: 'row',
    padding: '12px 16px'
  },
  articleBoxWrapper: {
    '@media screen and (max-width:480px)': {
      padding: '0 16px 24px'
    },
    padding: '0 32px 24px 32px',
    width: '100%'
  },
  articleDate: {
    '@media screen and (max-width:480px)': {
      mb: '6px'
    },
    color: blueGrayText,
    fontSize: '14px',
    mb: '8px'
  },
  articleImage: {
    '@media screen and (max-width:960px)': {
      width: '50%'
    },
    maxWidth: '298px'
  },
  articleTexts: {
    display: 'flex',
    flexDirection: 'column'
  },
  articleTitle: {
    '@media screen and (max-width:480px)': {
      fontSize: '14px'
    },
    color: black,
    fontSize: '16px',
    fontWeight: 700
  },
  buttonInner: {
    alignItems: 'center',
    columnGap: '8px',
    display: 'flex',
    flexDirection: 'row'
  },
  card: {
    '@media screen and (max-width:960px)': {
      borderRadius: '0 0 4px 4px'
    },
    border: '1px solid #DBDBE0',
    borderRadius: '0 4px 4px 0',
    boxShadow: 'none'
  },
  cardWrapper: {
    ':hover': {
      opacity: 0.7
    },
    '@media screen and (max-width:960px)': {
      flexDirection: 'column'
    },
    cursor: 'pointer',
    display: 'flex',
    flexDirection: 'row',
    flexWrap: 'nowrap',
    width: '100%'
  },
  cardcontent: {
    '&:last-child': {
      paddingBottom: 0
    },
    '@media screen and (max-width:960px)': {
      padding: '16px'
    },
    padding: '32px'
  },
  category: {
    color: blueGrayText,
    fontSize: '14px'
  },
  categoryTitle: {
    '@media screen and (max-width:480px)': { fontSize: '18px' },
    color: black,
    fontSize: '20px',
    fontWeight: 700,
    overflowWrap: 'anywhere'
  },
  checkbox: {
    '&.Mui-checked': {
      color: black
    },
    color: black
  },
  comparison: {
    '@media screen and (max-width:960px)': {
      borderRadius: '4px 4px 0 0',
      flexDirection: 'row',
      height: '40px',
      justifyContent: 'flex-end',
      padding: '8px 16px',
      width: '100%'
    },
    alignItems: 'center',
    bgcolor: '#DBDBE0',
    borderRadius: '4px 0 0 4px',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    width: '48px'
  },
  container: {
    alignItems: 'center',
    display: 'flex'
  },
  divider: {
    backgroundColor: '#DBDBE0',
    color: black,
    marginBottom: '8px',
    marginTop: '8px'
  },
  favoButton: {
    '&:hover': {
      backgroundColor: '#F2F2F2',
      boxShadow: 'none',
      color: black,
      opacity: 0.7
    },
    backgroundColor: '#F2F2F2',
    height: '36px',
    maxWidth: '178px',
    padding: '6px 16px'
  },
  favoLine: {
    height: '20px',
    width: '1px'
  },
  favoText: {
    fontSize: '14px'
  },
  generateCheck: {
    '@media screen and (min-width:480px)': { display: 'none' }
  },
  label: {
    backgroundColor: 'rgba(10, 126, 191, 0.08)',
    border: '1px solid ##0A7EBF',
    borderRadius: '4px',
    height: '24px'
  },
  mediaStatusTag: {
    '.MuiChip-label': {
      padding: '3px 16px'
    },
    backgroundColor: '#F3F3F3',
    border: '1px solid #757575',
    borderRadius: '4px',
    color: '#757575',
    fontSize: '12px',
    height: '24px',
    ml: '12px'
  },
  tags: {
    columnGap: '8px',
    rowGap: '8px'
  },
  text: {
    '@media screen and (max-width:480px)': { fontSize: '12px' },
    color: black,
    fontSize: '14px',
    overflowWrap: 'anywhere'
  }
};

interface Select {
  value: number;
  option: string;
}

interface IndustryProps {
  industry: {
    industriesNum: number;
    industriesOther: string;
    percentage: number;
  };
}

const MediaCard: React.FC<Props> = (props) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const {
    title,
    summaryText,
    funnel,
    mediaType,
    target,
    seasonalMenuMaterialText,
    image,
    demographicsGender,
    demographicsGeneration,
    category,
    hasCheck,
    industries,
    mediaId,
    screen,
    checked,
    onCheckboxChange,
    cantInquiry,
    mediaStatus
  } = props;
  const [open, setOpen] = useState(false);
  const [errorText, setErrorText] = useState('');

  const Categories = (): React.ReactElement => {
    const showCategory = (i: number): string => {
      const list: Select[] = categoryListForReference();
      const cates = list.find(
        (element: Select): boolean => element.value === i
      );
      return cates?.option ?? '';
    };

    let categoriesArray: string[] = [];
    if (typeof category === 'number') {
      const text = showCategory(category);
      categoriesArray.push(text);
    } else if (Array.isArray(category)) {
      categoriesArray = category.map(showCategory);
    }

    return (
      <Grid container flexDirection="row" columnGap="4px" rowGap="4px">
        {categoriesArray.map((item) => (
          <Chip
            key={item}
            sx={styles.label}
            label={item}
            color="primary"
            variant="outlined"
          />
        ))}
        {screen === screenType.publisher && (
          <Chip
            label={
              mediaStatus === status.approved
                ? t('status.approved')
                : mediaStatus === status.notChecked
                ? t('status.notChecked')
                : mediaStatus === status.fix
                ? t('status.fix')
                : t('status.deleted')
            }
            sx={[
              styles.mediaStatusTag,
              {
                '@media screen and (min-width:1201px)': {
                  display: 'none'
                },
                ml: 0,
                width: '95px'
              }
            ]}
          />
        )}
      </Grid>
    );
  };

  // ファネルcheck表示
  const updateChecked = (
    list: SelectOption[],
    val: number[] | undefined
  ): SelectOption[] => {
    if (!val) return list;
    list.forEach((item) => {
      if (val.includes(item.value)) {
        item.checked = true;
      }
    });
    return list;
  };

  const funnelChips = updateChecked(funnelListForReference(), funnel);

  // メディア種別表示
  const mTypeList = mediaTypeList();
  const showMediaType = (v: number): string => {
    const datas = mTypeList.map((item) =>
      item.options.find((i) => i.value === v)
    );
    const d = datas.filter((item) => item !== undefined);
    return d[0]?.option ?? '';
  };
  const mediaTypeText = showMediaType(mediaType);

  // ターゲット表示
  const tList = targetList();
  const showTarget = (itemValue: number | undefined): string => {
    const d = tList.find((element) => element.value === itemValue);
    return d?.option ?? '';
  };
  const targetText = target
    ? target.map((item: number) => showTarget(item))
    : [];

  const Industries = (industryProps: IndustryProps): React.ReactElement => {
    const { industry } = industryProps;

    function showMainHelperText(indust: {
      industriesNum: number;
      industriesOther: string;
      percentage: number | null | undefined;
    }): string {
      const list: Select[] = mediaIndustriesList();
      const selectedIndustry = list.find(
        (element: Select): boolean =>
          element.value === Number(indust.industriesNum)
      );
      if (Number(indust.industriesNum) === select.other) {
        if (indust.percentage && indust.percentage >= 1) {
          return `${indust.industriesOther}：${indust.percentage}%`;
        }
        return indust.industriesOther ?? '';
      }
      if (indust.percentage && indust.percentage >= 1) {
        return `${selectedIndustry?.option ?? ''}：${indust.percentage}%`;
      }
      return selectedIndustry?.option ?? '';
    }

    const industryString = showMainHelperText(industry);
    return (
      <Typography component="span" sx={styles.text}>
        {industryString}
      </Typography>
    );
  };

  // グラフの作成
  const genderOptions = {
    indexAxis: 'y' as const,
    plugins: {
      legend: {
        align: 'start' as const,
        labels: {
          boxHeight: 10,
          boxWidth: 10,
          textAlign: 'left' as const
        },
        onClick: function () {
          return false;
        },
        position: 'bottom' as const
      },
      title: {
        display: true,
        fontSize: '12px',
        text: t('media.demoTable.gender')
      }
    },
    responsive: true,
    scales: {
      x: {
        display: false,
        stacked: true
      },
      y: {
        display: false,
        stacked: true,
        ticks: {
          padding: -20
        }
      }
    }
  };

  const options = {
    indexAxis: 'y' as const,
    plugins: {
      legend: {
        align: 'start' as const,
        labels: {
          boxHeight: 10,
          boxWidth: 10,
          textAlign: 'left' as const
        },
        onClick: function () {
          return false;
        },
        position: 'bottom' as const
      },
      title: {
        display: true,
        fontSize: '12px',
        text: t('media.user')
      }
    },
    responsive: true,
    scales: {
      x: {
        display: false,
        stacked: true
      },
      y: {
        display: false,
        stacked: true,
        ticks: {
          padding: -20
        }
      }
    }
  };

  const genderData = {
    datasets: [
      {
        backgroundColor: '#6A00BD',
        barThickness: 8,
        data: [demographicsGender?.male],
        label: demographicsGender?.male
          ? `${t('media.genderOption.male')}：${demographicsGender?.male}％`
          : t('media.genderOption.male')
      },
      {
        backgroundColor: '#9358C2',
        barThickness: 8,
        data: [demographicsGender?.female],
        label: demographicsGender?.female
          ? `${t('media.genderOption.female')}：${demographicsGender?.female}％`
          : t('media.genderOption.female')
      }
    ],
    labels: ['']
  };

  const data = {
    datasets: [
      {
        backgroundColor: 'blue',
        barThickness: 8,
        data: [demographicsGeneration?.f10],
        label: demographicsGeneration?.f10
          ? `${t('media.geneOption.f10')}：${demographicsGeneration?.f10}％`
          : t('media.geneOption.f10')
      },
      {
        backgroundColor: 'blue',
        barThickness: 8,
        data: [demographicsGeneration?.f20],
        label: demographicsGeneration?.f20
          ? `${t('media.geneOption.f20')}：${demographicsGeneration?.f20}％`
          : t('media.geneOption.f20')
      },
      {
        backgroundColor: 'red',
        barThickness: 8,
        data: [demographicsGeneration?.f30],
        label: demographicsGeneration?.f30
          ? `${t('media.geneOption.f30')}：${demographicsGeneration?.f30}％`
          : t('media.geneOption.f30')
      },
      {
        backgroundColor: 'green',
        barThickness: 8,
        data: [demographicsGeneration?.f40],
        label: demographicsGeneration?.f40
          ? `${t('media.geneOption.f40')}：${demographicsGeneration?.f40}％`
          : t('media.geneOption.f40')
      },
      {
        backgroundColor: 'yellow',
        barThickness: 8,
        data: [demographicsGeneration?.f50],
        label: demographicsGeneration?.f50
          ? `${t('media.geneOption.f50')}：${demographicsGeneration?.f50}％`
          : t('media.geneOption.f50')
      },
      {
        backgroundColor: '#6A00BD',
        barThickness: 8,
        data: [demographicsGeneration?.f60],
        label: demographicsGeneration?.f60
          ? `${t('media.geneOption.f60')}：${demographicsGeneration?.f60}％`
          : t('media.geneOption.f60')
      },
      {
        backgroundColor: 'black',
        barThickness: 8,
        data: [demographicsGeneration?.f70],
        label: demographicsGeneration?.f70
          ? `${t('media.geneOption.f70')}：${demographicsGeneration?.f70}％`
          : t('media.geneOption.f70')
      },
      {
        backgroundColor: '#6A00BD',
        barThickness: 8,
        data: [demographicsGeneration?.m10],
        label: demographicsGeneration?.m10
          ? `${t('media.geneOption.m10')}：${demographicsGeneration?.m10}％`
          : t('media.geneOption.m10')
      },
      {
        backgroundColor: '#6A00BD',
        barThickness: 8,
        data: [demographicsGeneration?.m20],
        label: demographicsGeneration?.m20
          ? `${t('media.geneOption.m20')}：${demographicsGeneration?.m20}％`
          : t('media.geneOption.m20')
      },
      {
        backgroundColor: '#6A00BD',
        barThickness: 8,
        data: [demographicsGeneration?.m30],
        label: demographicsGeneration?.m30
          ? `${t('media.geneOption.m30')}：${demographicsGeneration?.m30}％`
          : t('media.geneOption.m30')
      },
      {
        backgroundColor: '#3965FF',
        barThickness: 8,
        data: [demographicsGeneration?.m40],
        label: demographicsGeneration?.m40
          ? `${t('media.geneOption.m40')}：${demographicsGeneration?.m40}％`
          : t('media.geneOption.m40')
      },
      {
        backgroundColor: '#3965FF',
        barThickness: 8,
        data: [demographicsGeneration?.m50],
        label: demographicsGeneration?.m50
          ? `${t('media.geneOption.m50')}：${demographicsGeneration?.m50}％`
          : t('media.geneOption.m50')
      },
      {
        backgroundColor: '#3965FF',
        barThickness: 8,
        data: [demographicsGeneration?.m60],
        label: demographicsGeneration?.m60
          ? `${t('media.geneOption.m60')}：${demographicsGeneration?.m60}％`
          : t('media.geneOption.m60')
      },
      {
        backgroundColor: '#3965FF',
        barThickness: 8,
        data: [demographicsGeneration?.m70],
        label: demographicsGeneration?.m70
          ? `${t('media.geneOption.m70')}：${demographicsGeneration?.m70}％`
          : t('media.geneOption.m70')
      }
    ],
    labels: ['']
  };

  const sort = (
    arr: Array<{
      label: string;
      data: Array<number | undefined>;
      backgroundColor: string;
      barThickness: number;
    }>
  ): Array<{
    label: string;
    data: Array<number | undefined>;
    backgroundColor: string;
    barThickness: number;
  }> => {
    arr.sort((a, b) =>
      a.data[0] !== undefined && b.data[0] !== undefined
        ? b.data[0] - a.data[0]
        : 0
    );
    const others = {
      backgroundColor: '#C4D5DE',
      barThickness: 8,
      data: [0],
      label: 'その他'
    };
    for (let i = 0; i < arr.length; i++) {
      if (i === 0) {
        arr[i].backgroundColor = '#1C31A1';
      } else if (i === 1) {
        arr[i].backgroundColor = '#4157CE';
      } else if (i === 2) {
        arr[i].backgroundColor = '#677CEF';
      } else if (i > 2) {
        others.data[0] += arr[i].data[0] as number;
      }
    }
    arr.push(others);
    const ranking = arr.filter(
      (e, i) => e.data[0] !== 0 && (i < 3 || e.label === 'その他')
    );
    return ranking;
  };

  data.datasets = sort(data.datasets);

  const { currentUser } = useContext(AuthContext);
  const [companyId, setCompanyId] = useState<string>('');
  const [mediaImage, setMediaImage] = useState<string>();

  useEffect(() => {
    void (async () => {
      if (currentUser) {
        const userRef = doc(usersRef, currentUser.uid);
        const userSnap = await getDoc(userRef);
        if (userSnap.exists()) {
          const ownCampanyRef = doc(
            companiesRef,
            userSnap.data()?.companyId as string
          );
          const companySnap = await getDoc(ownCampanyRef);
          if (companySnap.exists()) {
            setCompanyId(userSnap.data()?.companyId as string);
          }
        }
        if (image) {
          const img = await getMediaImage(image);
          setMediaImage(img ?? noImage);
        } else {
          setMediaImage(noImage);
        }
      }
    })();
  }, [image]);

  const [toggleDrawer, setToggleDrawer] = useState<boolean>(false);

  const handleDrawerToggle = (): void => {
    setToggleDrawer(!toggleDrawer);
  };

  const [favorite, setFavorite] = useState<boolean>(false);
  const [mediaFavoriteId, setMediaFavoriteId] = useState<string>('');
  const [favoriteCount, setFavoriteCount] = useState<number>();

  useEffect(() => {
    void (async () => {
      try {
        if (currentUser && mediaId) {
          const favoriteMedias = favoriteMediasRef(mediaId);
          const q = query(favoriteMedias, where('uid', '==', currentUser.uid));
          const querySnapshot = await getDocs(q);
          querySnapshot.docs.forEach((document) => {
            setMediaFavoriteId(document.id);
          });
          onSnapshot(q, (snap) => {
            setFavorite(!snap.empty);
          });
          const mediaRef = doc(mediasRef, mediaId);
          const mediaSnap = await getDoc(mediaRef);
          const count = mediaSnap.data()?.favoriteCount as number;
          setFavoriteCount(count);

          // お気に入り数のリアルタイム更新処理
          const unsubscribe = onSnapshot(mediaRef, (snap) => {
            const realtimeCount = snap.data()?.favoriteCount as number;
            setFavoriteCount(realtimeCount);
          });

          return () => unsubscribe();
        }
      } catch (e) {
        throwError(e);
      }
    })();
  }, [mediaId, favorite]);

  const [isFavoriteSubmitting, setIsFavoriteSubmitting] =
    useState<boolean>(false);

  const mediaFavoriteHandle = async (mId: string): Promise<void> => {
    try {
      setIsFavoriteSubmitting(true);
      if (currentUser) {
        if (favorite) {
          await deleteFavorite(mId, mediaFavoriteId);
          setFavorite(false);
        } else {
          await addFavoriteMedia(currentUser.uid, mId, companyId);
          setFavorite(true);
        }
      }
      setIsFavoriteSubmitting(false);
    } catch (e) {
      setIsFavoriteSubmitting(false);
      throwError(e);
    }
  };

  const [articleId, setArticleId] = useState<string>('');
  const [articleDate, setArticleDate] = useState<string>('');
  const [articleTitle, setArticleTitle] = useState<string>('');
  const [articleImage, setArticleImage] = useState<string>('');

  const formattedDate = (date: string | undefined): string | undefined => {
    if (date) {
      const d = new Date(date);
      const year = d.getFullYear();
      const month = d.getMonth() + 1;
      const day = d.getDate();
      return `${year}/${month}/${day}`;
    }
  };

  useEffect(() => {
    void (async () => {
      const mediaRef = doc(mediasRef, mediaId);
      const mediaSnap = await getDoc(mediaRef);
      const draftFlg = mediaSnap.data()?.draftFlg as boolean | undefined;
      if (draftFlg === false) {
        const id = mediaSnap.data()?.articleId as string;
        setArticleId(id);
        if (id === undefined) return;
        const articleData = await getPost(id);
        setArticleDate(formattedDate(articleData.createdAt) ?? '');
        setArticleTitle(articleData.title ?? '');
        const imagePath = await getMediaImage(
          articleData.imagePath as unknown as string
        );
        setArticleImage(imagePath ?? noImage);
      } else {
        setArticleId('');
        setArticleDate('');
        setArticleTitle('');
        setArticleImage('');
      }
    })();
  }, [mediaId]);

  return (
    <>
      <Grid
        sx={styles.cardWrapper}
        onClick={() => {
          if (!toggleDrawer && mediaId) {
            if (screen === screenType.client) {
              navigate(`/client/mypage/media/${mediaId}`);
            } else if (screen === screenType.publisher) {
              navigate(`/publisher/mypage/media/${mediaId}`);
            } else if (screen === screenType.agent) {
              navigate(`/agent/mypage/media/${mediaId}`);
            }
          }
        }}
      >
        {screen !== screenType.publisher && (
          <Grid sx={styles.comparison}>
            <Checkbox
              sx={styles.checkbox}
              checked={checked}
              onClick={(e) => {
                e.stopPropagation();
                if (onCheckboxChange) {
                  onCheckboxChange(mediaId);
                }
              }}
            />
            <Typography component="p" sx={{ color: black }}>
              {t('media.comparison')}
            </Typography>
          </Grid>
        )}
        <Grid sx={{ flex: 1 }}>
          <Card sx={styles.card}>
            <Grid
              container
              sx={{
                '@media screen and (max-width:960px)': {
                  flexDirection: 'column'
                }
              }}
            >
              <Grid
                item
                md={12}
                lg={4}
                sx={{
                  display: {
                    lg: 'block',
                    md: 'none',
                    sm: 'none',
                    xl: 'block',
                    xs: 'none'
                  }
                }}
              >
                <CardContent sx={{ padding: '32px 0 0 32px' }}>
                  <Grid container>
                    <Grid item xs={12}>
                      <img
                        src={mediaImage ?? noImage}
                        style={{
                          height: 'auto',
                          objectFit: 'contain',
                          width: '100%'
                        }}
                        alt="mediaImage"
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <Grid component="div" sx={{ mt: '8px' }}>
                        {demographicsGender && (
                          <Bar
                            width={300}
                            height={80}
                            options={genderOptions}
                            data={genderData}
                          />
                        )}
                        {demographicsGeneration && (
                          <Bar
                            width={300}
                            height={87}
                            options={options}
                            data={data}
                          />
                        )}
                      </Grid>
                    </Grid>
                  </Grid>
                </CardContent>
              </Grid>
              <Grid
                item
                md={12}
                lg={4}
                sx={{
                  display: {
                    lg: 'none',
                    md: 'block',
                    sm: 'block',
                    xl: 'none',
                    xs: 'block'
                  }
                }}
              >
                <CardContent sx={styles.cardcontent}>
                  <Grid container>
                    <Grid item xs={4} sm={4} md={4}>
                      <img
                        src={mediaImage ?? noImage}
                        style={{
                          height: 'auto',
                          objectFit: 'contain',
                          width: '100%'
                        }}
                        alt="mediaImage"
                      />
                    </Grid>
                    <Grid item xs={8} sm={8} md={8} sx={{ pl: '16px' }}>
                      <Categories />
                      <Typography sx={[styles.categoryTitle, { mt: '8px' }]}>
                        {title}
                      </Typography>
                    </Grid>
                    <Grid item xs={12}>
                      <Grid component="div" sx={{ mt: '8px' }}>
                        {demographicsGender && (
                          <Bar
                            width={300}
                            height={80}
                            options={genderOptions}
                            data={genderData}
                          />
                        )}
                        {demographicsGeneration && (
                          <Bar
                            width={300}
                            height={87}
                            options={options}
                            data={data}
                          />
                        )}
                      </Grid>
                    </Grid>
                  </Grid>
                </CardContent>
              </Grid>
              <Grid item xs={12} sm={12} md={12} lg={8}>
                <CardContent
                  sx={{
                    '@media screen and (max-width:960px)': {
                      padding: '16px'
                    },
                    padding: '32px'
                  }}
                >
                  <Typography component="div">
                    <Typography
                      component="div"
                      sx={{
                        display: {
                          lg: 'block',
                          md: 'none',
                          sm: 'none',
                          xl: 'block',
                          xs: 'none'
                        }
                      }}
                    >
                      <Categories />
                      <Grid
                        container
                        flexDirection={'row'}
                        justifyContent={'space-between'}
                        alignItems={'center'}
                      >
                        <Typography sx={[styles.categoryTitle, { mt: '8px' }]}>
                          {title}
                        </Typography>
                        {screen === screenType.publisher && (
                          <Chip
                            label={
                              mediaStatus === status.approved
                                ? t('status.approved')
                                : mediaStatus === status.notChecked
                                ? t('status.notChecked')
                                : mediaStatus === status.fix
                                ? t('status.fix')
                                : t('status.deleted')
                            }
                            sx={[
                              styles.mediaStatusTag,
                              {
                                '@media screen and (max-width:1200px)': {
                                  display: 'none'
                                },
                                ml: 0,
                                width: '95px'
                              }
                            ]}
                          />
                        )}
                      </Grid>
                    </Typography>
                    <Divider light sx={styles.divider} />
                    <Typography component="div">
                      <Grid
                        container
                        sx={{
                          '@media screen and (max-width:480px)': {
                            flexDirection: 'column'
                          }
                        }}
                      >
                        <Grid item sm={12} md={4} sx={styles.container}>
                          <Typography
                            component="div"
                            sx={[styles.text, { mb: '4px' }]}
                          >
                            {t('media.overview')}
                          </Typography>
                        </Grid>
                        <Grid item sm={12} md={8} sx={styles.text}>
                          {summaryText}
                        </Grid>
                      </Grid>
                    </Typography>
                    <Divider light sx={styles.divider} />
                    <Typography component="div">
                      <Grid container>
                        <Grid item sm={12} md={4} sx={styles.container}>
                          <Typography
                            component="div"
                            sx={[styles.text, { mb: '8px' }]}
                          >
                            {t('media.funnel')}
                          </Typography>
                        </Grid>
                        <Grid item sm={12} md={8} sx={styles.text}>
                          <Grid container flexDirection="row" sx={styles.tags}>
                            {funnelChips?.map((item) => (
                              <Chip
                                key={item.value}
                                label={item.option}
                                sx={{
                                  '@media screen and (max-width:480px)': {
                                    height: '28px'
                                  },
                                  backgroundColor: item.checked
                                    ? black
                                    : '#E0E0E0',
                                  color: item.checked ? '#FFFFFF' : '#757575',
                                  fontSize: '12px'
                                }}
                              />
                            ))}
                          </Grid>
                        </Grid>
                      </Grid>
                    </Typography>
                    <Divider light sx={styles.divider} />
                    <Typography component="div">
                      <Grid
                        container
                        sx={{
                          '@media screen and (max-width:600px)': {
                            flexDirection: 'column'
                          }
                        }}
                      >
                        <Grid item sm={12} md={4} sx={styles.container}>
                          <Typography component="div" sx={styles.text}>
                            {t('media.main')}
                          </Typography>
                        </Grid>
                        <Grid
                          item
                          sm={12}
                          md={8}
                          sx={[styles.text, { mb: '4px' }]}
                        >
                          {industries?.[0] && (
                            <Typography sx={styles.text}>
                              1. <Industries industry={industries[0]} />
                            </Typography>
                          )}
                          {industries?.[1] && (
                            <Typography sx={styles.text}>
                              2. <Industries industry={industries[1]} />
                            </Typography>
                          )}
                          {industries?.[2] && (
                            <Typography sx={styles.text}>
                              3. <Industries industry={industries[2]} />
                            </Typography>
                          )}
                        </Grid>
                      </Grid>
                    </Typography>
                    <Divider light sx={styles.divider} />
                    <Typography component="div">
                      <Grid
                        container
                        sx={{
                          '@media screen and (max-width:480px)': {
                            flexDirection: 'column'
                          }
                        }}
                      >
                        <Grid item sm={12} md={4} sx={styles.container}>
                          <Typography
                            component="div"
                            sx={[styles.text, { mb: '4px' }]}
                          >
                            {t('media.mediaType')}
                          </Typography>
                        </Grid>
                        <Grid item sm={12} md={8} sx={styles.text}>
                          {mediaTypeText}
                        </Grid>
                      </Grid>
                    </Typography>
                    <Divider light sx={styles.divider} />
                    <Typography component="div">
                      <Grid
                        container
                        sx={{
                          '@media screen and (max-width:480px)': {
                            flexDirection: 'column'
                          }
                        }}
                      >
                        <Grid item sm={12} md={4} sx={styles.container}>
                          <Typography
                            component="div"
                            sx={[styles.text, { mb: '4px' }]}
                          >
                            {t('media.targetTitle')}
                          </Typography>
                        </Grid>
                        <Grid item sm={12} md={8} sx={styles.text}>
                          {targetText.join('、')}
                        </Grid>
                      </Grid>
                    </Typography>
                    <Divider light sx={styles.divider} />
                    {seasonalMenuMaterialText && (
                      <Box
                        sx={{
                          bgcolor: '#FFEBEB',
                          borderRadius: '2px',
                          padding: '12px'
                        }}
                      >
                        <Grid
                          container
                          sx={{
                            '@media screen and (max-width:480px)': {
                              flexDirection: 'column'
                            }
                          }}
                        >
                          <Grid item sm={12} md={4} sx={styles.container}>
                            <SellOutlinedIcon
                              sx={{ color: primarycolor, mr: '2px' }}
                            />
                            <Typography
                              component="div"
                              sx={[
                                styles.text,
                                { color: primarycolor, mb: '4px' }
                              ]}
                            >
                              {t('media.seasonalMenuMaterialTitle')}
                            </Typography>
                          </Grid>
                          <Grid item sm={12} md={8} sx={styles.container}>
                            <Typography
                              component="div"
                              sx={[styles.text, { color: primarycolor }]}
                            >
                              {seasonalMenuMaterialText}
                            </Typography>
                          </Grid>
                          <Typography component="p"></Typography>
                        </Grid>
                      </Box>
                    )}
                  </Typography>
                </CardContent>
              </Grid>
              <Grid sx={styles.articleBoxWrapper}>
                {screen !== screenType.publisher && articleId && (
                  <Box
                    sx={styles.articleBox}
                    onClick={(e) => {
                      e.stopPropagation();
                      if (screen === screenType.client) {
                        navigate(`/client/mypage/article/${articleId}`);
                      } else if (screen === screenType.agent) {
                        navigate(`/agent/mypage/article/${articleId}`);
                      }
                    }}
                  >
                    <Grid sx={styles.articleImage}>
                      <img
                        src={articleImage ?? noImage}
                        style={{
                          height: 'auto',
                          objectFit: 'contain',
                          width: '100%'
                        }}
                        alt="mediaArticleImage"
                      />
                    </Grid>
                    <Grid sx={styles.articleTexts}>
                      <Typography component="div" sx={styles.articleDate}>
                        {`${t(
                          'mypage.articleList.createdDate'
                        )}：${articleDate}`}
                      </Typography>
                      <Typography component="div" sx={styles.articleTitle}>
                        {articleTitle}
                      </Typography>
                    </Grid>
                  </Box>
                )}
                {hasCheck && (
                  <Grid
                    container
                    columnGap="16px"
                    sx={{
                      '@media screen and (max-width:600px)': {
                        justifyContent: 'space-between'
                      },
                      justifyContent: 'flex-end',
                      marginTop: {
                        md: '24px',
                        sm: '16px',
                        xs: '16px'
                      },
                      textAlign: {
                        lg: 'right',
                        md: 'right',
                        sm: 'center',
                        xl: 'right',
                        xs: 'center'
                      }
                    }}
                  >
                    {screen !== screenType.publisher && (
                      <Button
                        sx={styles.favoButton}
                        onClick={(e) => {
                          e.stopPropagation();
                          mediaId && mediaFavoriteHandle(mediaId);
                        }}
                        disabled={isFavoriteSubmitting}
                      >
                        <Typography component="div" sx={styles.buttonInner}>
                          {!favorite ? (
                            <StarBorderRoundedIcon
                              sx={{
                                color: blueGrayText,
                                cursor: 'pointer'
                              }}
                            />
                          ) : (
                            <StarRoundedIcon
                              sx={{
                                color: '#FCBA04',
                                cursor: 'pointer'
                              }}
                            />
                          )}
                          <Typography
                            component="span"
                            sx={[styles.favoText, blackText]}
                          >
                            {t('media.favorite')}
                          </Typography>
                          <Divider
                            orientation="vertical"
                            sx={styles.favoLine}
                          />
                          <Typography
                            component="span"
                            sx={[styles.favoText, blackText]}
                          >
                            {favoriteCount ?? 0}
                          </Typography>
                        </Typography>
                      </Button>
                    )}
                    <Button
                      variant="contained"
                      sx={[
                        primaryButtonBackground,
                        {
                          '@media screen and (min-width:600px)': {
                            maxWidth: '200px'
                          },
                          flex: 1,
                          height: {
                            xs: '36px'
                          }
                        }
                      ]}
                      fullWidth
                      onClick={(e) => {
                        e.stopPropagation();
                        if (cantInquiry) {
                          setErrorText(cantInquiry);
                          setOpen(true);
                        } else {
                          handleDrawerToggle();
                        }
                      }}
                    >
                      <Typography sx={[styles.text, { color: 'white' }]}>
                        {t('media.send')}
                      </Typography>
                    </Button>
                  </Grid>
                )}
                {screen === screenType.publisher && (
                  <Grid sx={{ textAlign: 'end' }}>
                    <Button
                      variant="outlined"
                      color="inherit"
                      onClick={(e) => {
                        e.stopPropagation();
                        navigate(`/publisher/mypage/media/${mediaId}/edit1`);
                      }}
                    >
                      {t('given.edit')}
                    </Button>
                  </Grid>
                )}
              </Grid>
            </Grid>
          </Card>
        </Grid>
        <ContactDrawer
          screen={screen}
          mediaId={mediaId ?? ''}
          toggleDrawer={toggleDrawer}
          handleDrawerToggle={handleDrawerToggle}
          onClose={(e: React.MouseEvent<SVGSVGElement, MouseEvent>) => {
            e.stopPropagation();
            handleDrawerToggle();
          }}
        />
      </Grid>
      <CustomSnackbar
        open={open}
        autoHideDuration={6000}
        onClose={() => setOpen(false)}
        text={errorText}
      />
    </>
  );
};

export default MediaCard;
